/**
 * In-app confirm dialog (imsight#121): replaces the browser's native
 * confirm(), which Office add-in webviews do not support and which cannot be
 * styled or translated. Markup follows SignedOutDialog. All text is set via
 * textContent (callers pass already translated strings, no HTML). Dialogs open
 * one at a time through the shared dialog queue.
 */

import { enqueueDialog } from './dialog-queue';
import { queuedModal } from './queued-modal';

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Red confirm button; initial focus on Cancel. */
  danger: boolean;
}

let instanceCounter = 0;

/**
 * Show the dialog. Resolves true only when the confirm button is used; Cancel,
 * Esc, backdrop click, the close button or any other hide resolves false.
 * Resolves exactly once.
 */
export function confirmDialog({
  title,
  message,
  confirmLabel,
  cancelLabel,
  danger,
}: ConfirmDialogOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    enqueueDialog((done) => {
      try {
        openDialog(
          { title, message, confirmLabel, cancelLabel, danger },
          resolve,
          done
        );
      } catch {
        resolve(false);
        done();
      }
    });
  });
}

function openDialog(
  { title, message, confirmLabel, cancelLabel, danger }: ConfirmDialogOptions,
  resolve: (value: boolean) => void,
  done: () => void
): void {
  const id = `confirm-dialog-${++instanceCounter}`;

  const modal = document.createElement('div');
  modal.className = 'modal fade';
  modal.id = id;
  modal.tabIndex = -1;
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-labelledby', `${id}-title`);
  modal.setAttribute('aria-describedby', `${id}-message`);

  const dialog = document.createElement('div');
  dialog.className = 'modal-dialog modal-dialog-centered';
  const content = document.createElement('div');
  content.className = 'modal-content';

  const header = document.createElement('div');
  header.className = 'modal-header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'modal-title fs-5';
  titleEl.id = `${id}-title`;
  titleEl.textContent = title;
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'btn-close';
  close.setAttribute('aria-label', cancelLabel);
  header.append(titleEl, close);

  const body = document.createElement('div');
  body.className = 'modal-body';
  const messageEl = document.createElement('p');
  messageEl.className = 'mb-0';
  messageEl.id = `${id}-message`;
  messageEl.textContent = message;
  body.appendChild(messageEl);

  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'btn btn-outline-secondary';
  cancel.dataset.confirmAction = 'cancel';
  cancel.textContent = cancelLabel;
  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = danger ? 'btn btn-danger' : 'btn btn-primary';
  confirm.dataset.confirmAction = 'confirm';
  confirm.textContent = confirmLabel;
  footer.append(cancel, confirm);

  content.append(header, body, footer);
  dialog.appendChild(content);
  modal.appendChild(dialog);
  document.body.appendChild(modal);

  let settled = false;
  const settle = (value: boolean): void => {
    if (settled) return;
    settled = true;
    resolve(value);
  };
  const { hide, show } = queuedModal(modal, {
    done,
    onShown: () => (danger ? cancel : confirm).focus(),
    onClosed: () => settle(false),
  });

  confirm.addEventListener('click', () => {
    settle(true);
    hide();
  });
  cancel.addEventListener('click', () => {
    settle(false);
    hide();
  });
  close.addEventListener('click', () => {
    settle(false);
    hide();
  });
  // Esc, backdrop click or programmatic hide: anything but confirm is false.
  modal.addEventListener('hide.bs.modal', () => settle(false));

  show();
}
